import * as React from 'react';
import axios from 'axios';
import moment, { Moment } from 'moment';
import swal from 'sweetalert2';

import { JsonObject, OfferSettingsJsonType } from '@bb/domain/dist/types';
import type { CurrentUser } from '@/app-package';
import { User, UserRoles } from '@bb/users';
import errorReporter from '@app/errorReporter';
import { OfferController } from 'api';

import { Modal } from 'components';
import type { ListingDoc, OrgDoc } from '@bb/domain/dist/schemas';
import { Loading } from '../../react-common';
import {
  OfferAgent,
  OfferDisabledRegistration,
  OfferInformation,
  OfferIrrevocableDate,
  OfferAttachmentButton,
} from '../../DashboardComponents/OfferViewComponents';
import { NotificationOptions } from '../../DashboardComponents/AppointmentPropertyUpdate';
import {
  UNVERIFIED_AGENT_MODAL,
  UNVERIFIED_ADMIN_MODAL,
} from '../../constants';

type OfferDetailsForCoop = {
  offerCount: number;
  offerSettings: Pick<
    OfferSettingsJsonType,
    | 'acceptedAnytime'
    | 'offerDate'
    | 'otherInstructions'
    | 'preemptive'
    | 'registration'
    | 'requestIrrevocable'
  >;
  offerSubmissions: string;
};

type AttachmentForm = { url: string; filename: string };

type OfferViewContainerProps = {
  appointmentId: string;
  listing: JsonObject<
    Omit<ListingDoc, 'reps' | 'org'> & { reps: User[]; org: OrgDoc }
  >;
  user: CurrentUser;
};

type OfferViewContainerState = {
  isLoading: boolean;
  offer: {
    orgId: string;
    irrevocable: Date;
    listingId: string;
    attachmentForm801: { filename: string; url: string } | null;
    phone: string;
  };
  offerDetails: OfferDetailsForCoop;
  notificationOptions: NotificationOptions;
};

class OfferViewContainer extends React.Component<
  OfferViewContainerProps,
  OfferViewContainerState
> {
  constructor(props: OfferViewContainerProps) {
    super(props);

    const { listing } = this.props;

    this.state = {
      isLoading: true,
      offerDetails: null,
      notificationOptions: null,
      offer: {
        orgId: listing.org.id,
        irrevocable: moment().endOf('day').toDate(),
        listingId: listing.id,
        phone: '',
        attachmentForm801: null,
      },
    };
  }

  async componentDidMount() {
    const { appointmentId, listing, user } = this.props;

    if (user.isUnverified) {
      const isAgent = user.role === UserRoles.Agent;
      const isListingAgent =
        listing.reps.filter(rep => rep.id === user?.id).length > 0;
      const redirectAccess = isAgent && !isListingAgent;

      Modal.warning({
        ...(isAgent ? UNVERIFIED_AGENT_MODAL : UNVERIFIED_ADMIN_MODAL),
        onOk:
          redirectAccess &&
          (() => {
            window.location.href = `#/listing/${listing.id}/view`;
          }),
      });

      if (redirectAccess) return;
    }

    const [{ data: offerDetails }, { data: notificationOptions }] =
      await Promise.all([
        axios.get<OfferDetailsForCoop>(
          `/user/coop/appointment/${appointmentId}/offer_details/${listing.id}`,
        ),
        axios.get<NotificationOptions>(
          `/user/current/listing_notification_setting/${listing.id}`,
        ),
      ]);

    this.setState({ isLoading: false, offerDetails, notificationOptions });
  }

  hasAllRequiredFields = () => {
    const { offer, offerDetails } = this.state;
    const { offerSettings } = offerDetails;

    let check = !!(offer.irrevocable && offer.phone);

    if (offerSettings.registration.shouldSubmitOfferDoc) {
      check &&= !!offer.attachmentForm801;
    }

    return check;
  };

  registerOffer = async () => {
    const { listing } = this.props;
    const { offer, offerDetails, notificationOptions } = this.state;
    const { offerSettings } = offerDetails;

    if (!this.hasAllRequiredFields()) {
      return swal.fire({
        title: 'Missing Required Fields',
        text: 'Please fill out the required fields',
        type: 'warning',
      });
    }

    if (offerSettings.registration.shouldSubmitOfferDoc) {
      const { value } = await swal.fire({
        title: 'VALID OFFER DOC',
        text: 'By clicking "Register My Offer", you verify that the file you have attached satisfies the document requirements for offer registration set by your real estate board.',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Register My Offer',
        cancelButtonText: 'Cancel',
      });

      if (!value) return;
    }

    this.setState({ isLoading: true });
    try {
      await Promise.all([
        OfferController.registerOfferForCoop(listing.id, offer),
        axios.post(
          `/user/current/listing_notification_setting/${listing.id}`,
          notificationOptions,
        ),
      ]);

      this.setState({ isLoading: false }, () => {
        swal.fire({ title: 'Offer Registered!', type: 'success' });
      });
    } catch (err) {
      errorReporter.error(err);

      this.setState({ isLoading: false }, () => {
        swal.fire({ title: 'Failed to register an offer', type: 'warning' });
      });
    }
  };

  checkOfferRegistrationEnabled = () => {
    const { offerDetails } = this.state;
    if (!offerDetails || !offerDetails.offerSettings) return false;
    const { offerSettings } = offerDetails;
    if (
      !offerSettings.registration?.isEnabled ||
      !offerSettings.registration?.shouldAllowMls
    )
      return false;
    if (offerSettings.acceptedAnytime) return true;

    const startOfDay = new Date(offerSettings.offerDate);
    startOfDay.setMinutes(1);
    startOfDay.setHours(0);

    if (offerSettings.offerDate && startOfDay < new Date()) {
      return true;
    }

    if (offerSettings.preemptive) {
      return true;
    }

    return false;
  };

  onPhoneNumberChange = (newNumber: string) => {
    const { offer } = this.state;

    this.setState({ offer: { ...offer, phone: newNumber } });
  };

  onAttachmentUpdate = (attachment: AttachmentForm) => {
    const { offer } = this.state;

    this.setState({ offer: { ...offer, attachmentForm801: attachment } });
  };

  onDateChange = (date: Moment) => {
    const { offer } = this.state;
    const newDate = moment(date).endOf('day').toDate();

    this.setState({ offer: { ...offer, irrevocable: newDate } });
  };

  onTimeChange = (time: Moment) => {
    const { offer } = this.state;
    const hour = moment(time || new Date()).hours();
    const min = moment(time || new Date()).minutes();
    const offerIrrevocable = moment(offer.irrevocable)
      .hours(hour)
      .minutes(min)
      .seconds(0)
      .milliseconds(0)
      .toDate();

    this.setState({ offer: { ...offer, irrevocable: offerIrrevocable } });
  };

  onRegisteredOffersNotificationChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.checked;
    const { notificationOptions } = this.state;

    this.setState({
      notificationOptions: { ...notificationOptions, registeredOffers: value },
    });
  };

  render() {
    const { listing, user } = this.props;
    const { isLoading, offer, offerDetails, notificationOptions } = this.state;

    if (isLoading) return <Loading size="fa-5x" />;

    const { offerSettings } = offerDetails;
    const listingAddress = `${listing.line1} ${listing.line2 || ''}`;
    const canRegisterOffer = this.checkOfferRegistrationEnabled();
    const saveButton = canRegisterOffer ? (
      <div key={0} className="pull-right">
        <button
          style={{ marginBottom: '6px' }}
          className="btn btn-success margin-right pull-right"
          onClick={this.registerOffer}
          disabled={!this.hasAllRequiredFields()}
        >
          Register Offer
        </button>
        <br />
        <label style={{ fontSize: '12px' }}>
          <input
            type="checkbox"
            checked={notificationOptions.registeredOffers}
            onChange={this.onRegisteredOffersNotificationChange}
          />{' '}
          Notify me regarding registered offers
        </label>
      </div>
    ) : null;

    return (
      <>
        <div style={{ display: 'flex' }}>
          <h2 className="font-light m-b-xs">{`${listingAddress} - Offers`}</h2>
          <div className="panel-header-extra">{saveButton}</div>
        </div>
        <hr style={{ marginTop: '10px', marginBottom: '10px' }} />
        <div style={{ width: '100%' }}>
          {!offerSettings.registration.isEnabled ||
          !offerSettings.registration.shouldAllowMls ? (
            <div className="row">
              <div className="col-md-6">
                <OfferDisabledRegistration listing={listing} />
              </div>
            </div>
          ) : (
            <div className="row">
              <div className="col-md-6">
                <OfferInformation {...offerDetails} />
              </div>
              <div className="col-md-6">
                {canRegisterOffer ? (
                  <>
                    <h3>Register Your Offer</h3>
                    <hr />

                    <div className="row p-r-xl">
                      <div className="col-sm-6 col-md-12 col-lg-6">
                        <OfferAgent
                          user={user}
                          phoneNumber={offer.phone}
                          onPhoneNumberChange={this.onPhoneNumberChange}
                        />
                        {offerSettings.registration.shouldSubmitOfferDoc && (
                          <OfferAttachmentButton
                            attachmentForm={offer.attachmentForm801}
                            onAttachmentUpdate={this.onAttachmentUpdate}
                            required
                          />
                        )}
                      </div>
                      <div className="col-sm-6 col-md-12 col-lg-6">
                        <OfferIrrevocableDate
                          irrevocableDate={offer.irrevocable}
                          onDateChange={this.onDateChange}
                          onTimeChange={this.onTimeChange}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <h3>Register Offer</h3>
                    <hr />

                    <h5>
                      Online offer registration will be available on the offer
                      date{' '}
                      {moment(offerDetails.offerSettings.offerDate).format(
                        'MMM DD, YYYY',
                      )}
                      .
                    </h5>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </>
    );
  }
}

export default OfferViewContainer;
